import React, { useCallback, useEffect, useState } from "react";
import { Activity, Users2, Boxes, CalendarRange, Search, ChevronLeft, ChevronRight, RotateCcw, Eye } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDate, wibDateISO, wibDaysAgo } from "../lib/api";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const num = (n) => new Intl.NumberFormat("id-ID").format(Number(n) || 0);
const monthStart = () => wibDaysAgo(30);
const today = () => wibDateISO();

const ACTION_STYLE = {
  create: "bg-green-100 text-green-700", update: "bg-amber-100 text-amber-700",
  delete: "bg-red-100 text-red-700", confirm: "bg-blue-100 text-blue-700",
  cancel: "bg-red-100 text-red-700", bulk_update: "bg-purple-100 text-purple-700",
};
const actionCls = (a) => ACTION_STYLE[a] || "bg-taupe/20 text-cocoa";

function StatCard({ icon: Icon, value, label, tint }) {
  return (
    <div className="rounded-xl border border-taupe/25 bg-white p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tint}`}><Icon className="w-5 h-5" /></div>
      <div><div className="text-2xl font-bold text-cocoa leading-none">{value}</div><div className="text-xs text-wmuted mt-1">{label}</div></div>
    </div>
  );
}

export default function UserActivity() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 0, limit: 15, stats: {}, users: [], all_modules: [] });
  const [f, setF] = useState({ q: "", user_id: "all", module: "all", start: monthStart(), end: today() });
  const [qInput, setQInput] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/users/activity", { params: {
      q: f.q || undefined, user_id: f.user_id, module: f.module,
      start: f.start || undefined, end: f.end || undefined, page, limit: 15,
    } }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
  }, [f, page]);
  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => { setF({ ...f, [k]: v }); setPage(1); };
  const clearFilters = () => { setF({ q: "", user_id: "all", module: "all", start: monthStart(), end: today() }); setQInput(""); setPage(1); };
  const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div data-testid="user-activity-page">
      <PageHeader title="User Activities" subtitle="Detail semua aktivitas yang dilakukan pengguna di seluruh modul">
        <Button variant="outline" onClick={clearFilters} className="border-taupe text-cocoa" data-testid="ua-clear-filters"><RotateCcw className="w-4 h-4 mr-1.5" />Clear Filters</Button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <StatCard icon={Activity} value={num(data.stats?.total_activities)} label="Total Activities" tint="bg-blue-100 text-blue-600" />
        <StatCard icon={Users2} value={num(data.stats?.active_users)} label="Active Users" tint="bg-green-100 text-green-600" />
        <StatCard icon={Boxes} value={num(data.stats?.modules)} label="Modules" tint="bg-purple-100 text-purple-600" />
        <StatCard icon={CalendarRange} value={num(data.stats?.days_range)} label="Days Range" tint="bg-amber-100 text-amber-600" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div>
          <Label className="text-xs">Search</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
            <Input value={qInput} onChange={(e) => setQInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && setFilter("q", qInput)} placeholder="Search activities..." className="pl-9 bg-white" data-testid="ua-search" />
          </div>
        </div>
        <div>
          <Label className="text-xs">User</Label>
          <Select value={f.user_id} onValueChange={(v) => setFilter("user_id", v)}>
            <SelectTrigger className="bg-white" data-testid="ua-user-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72"><SelectItem value="all">All Users</SelectItem>{(data.users || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Module</Label>
          <Select value={f.module} onValueChange={(v) => setFilter("module", v)}>
            <SelectTrigger className="bg-white" data-testid="ua-module-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72"><SelectItem value="all">All Modules</SelectItem>{(data.all_modules || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Start Date</Label><Input type="date" value={f.start} onChange={(e) => setFilter("start", e.target.value)} className="bg-white" data-testid="ua-start" /></div>
          <div><Label className="text-xs">End Date</Label><Input type="date" value={f.end} onChange={(e) => setFilter("end", e.target.value)} className="bg-white" data-testid="ua-end" /></div>
        </div>
      </div>

      <Panel>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-12">#</TableHead><TableHead>User</TableHead><TableHead>Action</TableHead>
              <TableHead>Module</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Date &amp; Time</TableHead><TableHead className="text-center">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((r, i) => (
              <TableRow key={r.id} data-testid={`ua-row-${r.id}`}>
                <TableCell className="text-wmuted">{(data.page - 1) * data.limit + i + 1}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-cocoa/10 text-cocoa flex items-center justify-center text-[11px] font-bold">{initials(r.user_name)}</div>
                    <span className="text-sm">{r.user_name || "—"}</span>
                  </div>
                </TableCell>
                <TableCell><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${actionCls(r.action)}`}>{r.action}</span></TableCell>
                <TableCell><span className="px-2 py-0.5 rounded-md text-xs bg-taupe/15 text-cocoa">{r.entity || "-"}</span></TableCell>
                <TableCell className="text-sm text-cocoa/80 max-w-md truncate" title={r.description}>{r.description}</TableCell>
                <TableCell className="text-right text-xs text-wmuted whitespace-nowrap">{fmtDate(r.datetime)}</TableCell>
                <TableCell className="text-center"><Button size="icon" variant="ghost" onClick={() => setDetail(r)} className="text-cocoa h-8 w-8" data-testid={`ua-detail-${r.id}`}><Eye className="w-4 h-4" /></Button></TableCell>
              </TableRow>
            ))}
            {!data.items.length && <TableRow><TableCell colSpan={7} className="text-center text-wmuted py-10"><Activity className="w-6 h-6 mx-auto mb-2 opacity-50" />{loading ? "Memuat..." : "Tidak ada aktivitas."}</TableCell></TableRow>}
          </TableBody>
        </Table>
        <div className="flex items-center justify-between px-4 py-3 border-t border-taupe/15 text-sm">
          <span className="text-wmuted">Showing {data.items.length ? (data.page - 1) * data.limit + 1 : 0}–{(data.page - 1) * data.limit + data.items.length} of {num(data.total)} entries</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} className="border-taupe text-cocoa" data-testid="ua-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-wmuted">Page {data.page}/{data.pages || 1}</span>
            <Button size="sm" variant="outline" disabled={page >= (data.pages || 1)} onClick={() => setPage(page + 1)} className="border-taupe text-cocoa" data-testid="ua-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </Panel>
      <ActivityDetail row={detail} setRow={setDetail} actionCls={actionCls} />
    </div>
  );
}

const fmtVal = (v) => {
  if (v === null || v === undefined) return "NULL";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

function DetailBlock({ details }) {
  const entries = details && typeof details === "object" ? Object.entries(details) : [];
  if (!entries.length) return <div className="text-sm text-wmuted italic">Tidak ada data perubahan yang tercatat.</div>;
  return (
    <div className="rounded-lg bg-red-50/60 border border-red-100 p-3 max-h-72 overflow-y-auto space-y-2">
      {entries.map(([k, v]) => {
        if (Array.isArray(v)) {
          const cols = v.length && typeof v[0] === "object" ? Object.keys(v[0]) : null;
          return (
            <div key={k}>
              <div className="text-xs font-semibold text-cocoa capitalize mb-1">{k}:</div>
              {v.length === 0 ? <div className="text-xs italic text-wmuted">[]</div> : cols ? (
                <div className="overflow-x-auto"><table className="w-full text-xs border-collapse">
                  <thead><tr>{cols.map((c) => <th key={c} className="border border-red-100 px-1.5 py-0.5 text-left bg-white capitalize">{c}</th>)}</tr></thead>
                  <tbody>{v.map((row, i) => <tr key={i}>{cols.map((c) => <td key={c} className="border border-red-100 px-1.5 py-0.5">{fmtVal(row[c])}</td>)}</tr>)}</tbody>
                </table></div>
              ) : <div className="text-xs text-cocoa/80">{v.map((x) => fmtVal(x)).join(", ")}</div>}
            </div>
          );
        }
        return <div key={k} className="flex flex-wrap gap-1"><span className="text-xs font-semibold text-cocoa capitalize">{k}:</span><span className="text-xs text-cocoa/80 italic">{fmtVal(v)}</span></div>;
      })}
    </div>
  );
}

function ActivityDetail({ row, setRow, actionCls }) {
  const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
  return (
    <Dialog open={!!row} onOpenChange={(v) => !v && setRow(null)}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="ua-detail-dialog">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Activity Details</DialogTitle></DialogHeader>
        {row && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              <div><div className="text-xs text-wmuted mb-0.5">User</div><div className="flex items-center gap-2"><div className="w-6 h-6 rounded-full bg-cocoa/10 text-cocoa flex items-center justify-center text-[10px] font-bold">{initials(row.user_name)}</div><span className="font-medium text-cocoa">{row.user_name || "—"}</span></div></div>
              <div><div className="text-xs text-wmuted mb-0.5">Date &amp; Time</div><div className="text-cocoa">{fmtDate(row.datetime)}</div></div>
              <div><div className="text-xs text-wmuted mb-0.5">Action</div><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${actionCls(row.action)}`}>{row.action}</span></div>
              <div><div className="text-xs text-wmuted mb-0.5">IP Address</div><div className="text-cocoa font-mono text-xs">{row.ip || "—"}</div></div>
              <div><div className="text-xs text-wmuted mb-0.5">Module</div><span className="px-2 py-0.5 rounded-md text-xs bg-taupe/15 text-cocoa">{row.entity || "—"}</span></div>
              <div><div className="text-xs text-wmuted mb-0.5">User Agent</div><div className="text-cocoa/80 text-xs break-words">{row.user_agent || "—"}</div></div>
            </div>
            <div><div className="text-xs text-wmuted mb-0.5">Description</div><div className="text-cocoa">{row.description}</div></div>
            <div><div className="text-xs font-semibold text-status-reject mb-1 flex items-center gap-1">Detail Perubahan</div><DetailBlock details={row.details} /></div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={() => setRow(null)} className="border-taupe text-cocoa">Cancel</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
